"use client";

/**
 * TimeZoneClock — the menu-bar clock.
 *
 * Renders the current time in the user's selected zone (see `TimeProvider`) and
 * opens a searchable menu to change it: the full IANA zone list, the active
 * zone marked, and a one-click "Use local" reset. This is the single control
 * that drives time display everywhere.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  formatInTimeZone,
  humanizeTimeZone,
  listTimeZones,
  localTimeZone,
  useTimezone,
} from "@itoresearch/design-system";
import { CheckIcon, ClockIcon } from "lucide-react";

/** Menu-bar clock format: weekday, date, and seconds (live). */
const CLOCK_FORMAT: Intl.DateTimeFormatOptions = {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};

export function TimeZoneClock() {
  const { timeZone, setTimeZone, useLocalTimeZone } = useTimezone();
  const [now, setNow] = useState(() => new Date());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const label = formatInTimeZone(now, timeZone, CLOCK_FORMAT);
  const local = useMemo(() => localTimeZone(), []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Clock, ${timeZone}. Change time zone`}
            title={`${humanizeTimeZone(timeZone)} — change time zone`}
            className="hidden h-7 items-center gap-1.5 px-2 font-normal tabular-nums text-muted-foreground sm:inline-flex"
          >
            <ClockIcon className="size-3.5" />
            <span>{label}</span>
          </Button>
        }
      />
      <PopoverContent align="end" className="w-80 p-0">
        <TimeZoneMenu
          timeZone={timeZone}
          localZone={local}
          onSelect={(zone) => {
            setTimeZone(zone);
            setOpen(false);
          }}
          onUseLocal={() => {
            useLocalTimeZone();
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function TimeZoneMenu({
  timeZone,
  localZone,
  onSelect,
  onUseLocal,
}: {
  timeZone: string;
  localZone: string;
  onSelect: (zone: string) => void;
  onUseLocal: () => void;
}) {
  const zones = useMemo(() => listTimeZones(), []);
  const isLocal = timeZone === localZone;

  return (
    <Command className="rounded-none! bg-transparent" loop>
      <CommandInput autoFocus placeholder="Search time zones…" />
      <CommandList className="max-h-72">
        <CommandEmpty>No matching time zone.</CommandEmpty>
        <CommandGroup heading="Time zone">
          {zones.map((zone) => (
            <CommandItem
              key={zone}
              value={zone}
              keywords={[humanizeTimeZone(zone).replace(/ · /g, " / ")]}
              onSelect={() => onSelect(zone)}
            >
              <span className="flex-1 truncate">{humanizeTimeZone(zone)}</span>
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">
                {zone}
              </span>
              {zone === timeZone ? (
                <CheckIcon className="size-3.5 shrink-0" />
              ) : null}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      <div className="flex items-center justify-between gap-2 border-t px-3 py-1.5 text-xs text-muted-foreground">
        <span className="truncate">
          {isLocal
            ? "Following your local time zone"
            : `Local: ${humanizeTimeZone(localZone)}`}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 px-2 text-xs"
          disabled={isLocal}
          onClick={onUseLocal}
        >
          Use local
        </Button>
      </div>
    </Command>
  );
}
